/**
 * Frontend Clinic Manager
 * Handles clinic operations through the multiClinicUserManager API
 */
import { apiClient } from './apiClient';

export class ClinicManager {
  constructor(apiEndpoint = null) {
    this.useMockData = import.meta.env.MODE === 'development' && !import.meta.env.VITE_API_ENDPOINT;
  }

  // No custom headers needed when using Amplify REST API with Cognito authorizer

  /**
   * List all clinics (preferred method for loading all clinics)
   * @param {number} limit - Maximum number of clinics to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object>} Clinic configurations object with paging info
   */
  async listClinics(limit = 100, offset = 0) {
    const data = await apiClient.clinics({ operation: 'listClinics', limit, offset });
    return data;
  }

  /**
   * Search clinics by prefix
   * @param {string} prefix - Search prefix (empty string lists all)
   * @param {number} limit - Maximum number of clinics to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise<Object[]>} Array of matching clinics
   */
  async searchClinics(prefix = '', limit = 25, offset = 0) {
    // If prefix is empty, use listClinics instead
    if (!prefix || prefix.trim() === '') {
      const result = await this.listClinics(limit, offset);
      return result.clinics ? Object.entries(result.clinics).map(([id, clinic]) => ({ ...clinic, id })) : [];
    }

    const data = await apiClient.clinics({ operation: 'searchClinics', prefix, limit, offset });
    // Map clinicId to id for consistency
    return (data.clinics || []).map(c => ({ ...c, id: c.clinicId || c.id }));
  }

  /**
   * Get all clinic configurations
   * @returns {Promise<Object>} Clinic configurations object
   */
  async getAllClinics() {
    if (this.useMockData) {
      return {
        clinics: {
          'brainqtech': {
            name: 'BrainQ Technologies',
            topics: ['emagine', 'qtherapy', 'default'],
            metadata: { type: 'corporate' },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          'default-clinic': {
            name: 'Default Clinic',
            topics: ['default'],
            metadata: { type: 'default' },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        },
        globalTopics: ['emagine', 'qtherapy', 'default']
      };
    }

    try {
      const data = await apiClient.clinics({ operation: 'getAllClinics' });
      return {
        clinics: data.clinics || {},
        globalTopics: data.globalTopics || ['emagine', 'qtherapy', 'default']
      };
    } catch (error) {
      console.error('Error getting all clinics:', error);
      throw new Error(`Failed to retrieve clinic configurations: ${error.message}`);
    }
  }

  /**
   * Get specific clinic configuration
   * @param {string} clinicId - The clinic ID
   * @returns {Promise<Object|null>} Clinic configuration or null if not found
   */
  async getClinic(clinicId) {
    if (this.useMockData) {
      const allClinics = await this.getAllClinics();
      return allClinics.clinics[clinicId] || null;
    }

    try {
      const data = await apiClient.clinics({ operation: 'getClinic', clinicId });
      return data.clinic || null;
    } catch (error) {
      console.error(`Error getting clinic ${clinicId}:`, error);
      throw new Error(`Failed to retrieve clinic ${clinicId}: ${error.message}`);
    }
  }

  /**
   * Create or update a clinic
   * @param {string} clinicId - The clinic ID
   * @param {Object} clinicData - Clinic configuration data
   * @returns {Promise<Object>} Updated clinic configuration
   */
  async createOrUpdateClinic(clinicId, clinicData) {
    console.log('clinicManager.createOrUpdateClinic called with:', { clinicId, clinicData });
    
    if (this.useMockData) {
      console.log(`Mock: Would update clinic ${clinicId}:`, clinicData);
      return {
        ...clinicData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }

    try {
      const body = { operation: 'createOrUpdateClinic', clinicData };
      if (clinicId) body.clinicId = clinicId;
      
      console.log('Sending request to apiClient.clinics:', body);
      const data = await apiClient.clinics(body);
      console.log('Response from apiClient.clinics:', data);
      
      return data.clinic || null;
    } catch (error) {
      console.error(`Error creating/updating clinic ${clinicId}:`, error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      throw new Error(`Failed to create/update clinic ${clinicId}: ${error.message}`);
    }
  }

  /**
   * Get topics available for a clinic
   * @param {string} clinicId - The clinic ID
   * @returns {Promise<string[]>} Array of available topics
   */
  async getClinicTopics(clinicId) {
    try {
      const clinic = await this.getClinic(clinicId);
      if (!clinic) {
        throw new Error(`Clinic ${clinicId} not found`);
      }
      return clinic.topics || [];
    } catch (error) {
      console.error(`Error getting topics for clinic ${clinicId}:`, error);
      throw new Error(`Failed to get topics for clinic ${clinicId}: ${error.message}`);
    }
  }

  /**
   * Get all available topics (global topics list)
   * @returns {Promise<string[]>} Array of global topics
   */
  async getGlobalTopics() {
    if (this.useMockData) {
      return ['emagine', 'qtherapy', 'default'];
    }

    try {
      const config = await this.getAllClinics();
      return config.globalTopics || ['emagine', 'qtherapy', 'default'];
    } catch (error) {
      console.error('Error getting global topics:', error);
      return ['emagine', 'qtherapy', 'default'];
    }
  }

  /**
   * Delete a clinic (soft-delete)
   */
  async deleteClinic(clinicId) {
    if (!clinicId) {
      throw new Error('Clinic ID is required');
    }
    try {
      const data = await apiClient.clinics({ operation: 'deleteClinic', clinicId });
      if (!data.success) throw new Error(data.error || 'Failed to delete clinic');
      return true;
    } catch (error) {
      console.error(`Error deleting clinic ${clinicId}:`, error);
      throw new Error(`Failed to delete clinic ${clinicId}: ${error.message}`);
    }
  }

  /**
   * Validate clinic ID format
   * @param {string} clinicId - The clinic ID to validate
   * @returns {boolean} True if valid
   */
  isValidClinicId(clinicId) {
    if (!clinicId || typeof clinicId !== 'string') {
      return false;
    }
    
    // Must be lowercase alphanumeric with hyphens, no spaces or special chars
    return /^[a-z0-9-]+$/.test(clinicId) && clinicId.length > 0;
  }

  /**
   * Generate S3 path for clinic data
   * @param {string} clinicId - The clinic ID
   * @param {string} topic - The topic name
   * @param {string} fileName - The file name
   * @returns {string} S3 path
   */
  generateS3Path(clinicId, topic, fileName) {
    if (!this.isValidClinicId(clinicId)) {
      throw new Error('Invalid clinic ID for S3 path generation');
    }

    // Sanitize inputs for S3 path safety
    const sanitizedTopic = topic.replace(/[^a-zA-Z0-9-_]/g, '');
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9-_.]/g, '');

    return `clinics/${clinicId}/${sanitizedTopic}/${sanitizedFileName}`;
  }
}

// Export singleton instance
export const clinicManager = new ClinicManager();
